import { useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { project, type CapacityMode, type FillMode } from '@rundown/domain';
import { reorderByRank, useOptimisticMutation } from '../../lib/optimistic';
import {
  backlogKeys,
  isInternalBacklogRow,
  rankBacklogItem,
  type BacklogRow,
  type ProjectionResponse,
} from './api';

export interface RankMoveVariables {
  readonly movedId: string;
  readonly beforeId?: string;
  readonly afterId?: string;
}

/**
 * Drag-to-reorder for the Backlog, wired through the shared optimistic
 * mutation helper (apps/web/src/lib/optimistic.ts).
 *
 * Two caches move together on every drag, not one: the ranked list itself,
 * and the capacity projection that depends on its order. The projection is
 * recomputed through the identical `project()` call the server uses — same
 * sprints, same capacity, only the item order differs — so "would land in
 * Sprint 21" updates the instant a card moves rather than only after the
 * round trip confirms it. `reconcile` then leaves that recompute standing:
 * it was already correct, computed by the same pure function, so there is
 * nothing the server's response adds beyond the row it changed.
 */
export function useBacklogRank(
  workspaceId: string,
  capacityMode: CapacityMode,
  fill: FillMode,
): UseMutationResult<BacklogRow, Error, RankMoveVariables> {
  const queryClient = useQueryClient();
  const listKey = backlogKeys.list(workspaceId);
  const projectionKey = backlogKeys.projection(workspaceId, capacityMode, fill);

  return useOptimisticMutation<RankMoveVariables, BacklogRow>({
    queryClient,
    mutationFn: ({ movedId, beforeId, afterId }) =>
      rankBacklogItem(workspaceId, movedId, { beforeId, afterId }),
    affectedQueryKeys: [listKey, projectionKey],
    applyOptimistically: (qc, variables) => {
      const previousList = qc.getQueryData<BacklogRow[]>(listKey);
      if (previousList === undefined) return;

      const reordered = reorderByRank(
        previousList,
        variables.movedId,
        variables,
        (row) => row.id,
        (row) => row.backlogRank,
        (row, backlogRank) => ({ ...row, backlogRank }),
      );
      qc.setQueryData(listKey, reordered);

      const projection = qc.getQueryData<ProjectionResponse>(projectionKey);
      if (projection === undefined) return;

      const keyBySprintId = new Map(projection.sprints.map((sprint) => [sprint.id, sprint.key]));
      const recomputed = project({
        // `reordered` is already highest-rank-first, exactly what
        // ProjectionInput.items requires — see reorderByRank's contract.
        items: reordered.map((row) => ({
          id: row.id,
          hours: row.approxHours,
          ownerId: isInternalBacklogRow(row) ? row.ownerId : null,
          ownSprint: row.ownSprint,
          pinnedSprint: row.placedSprintId === null ? null : (keyBySprintId.get(row.placedSprintId) ?? null),
        })),
        sprints: projection.sprints.map((sprint) => ({
          key: sprint.key,
          locked: sprint.state === 'locked',
          committedHours: sprint.committedHours,
          unfinishedHours: sprint.unfinishedHours,
        })),
        plannableHours: projection.plannableHours,
        capacityMode: projection.capacityMode,
        ownerPlannableHours: projection.ownerPlannableHours,
        fill: projection.fill,
      });

      qc.setQueryData<ProjectionResponse>(projectionKey, { ...projection, ...recomputed });
    },
    reconcile: (qc, result) => {
      const current = qc.getQueryData<BacklogRow[]>(listKey);
      if (current === undefined) return;
      // Merge the server's row (its authoritative backlogRank included) into
      // the list already showing it in the right place — not an invalidate,
      // which would refetch and flicker. See optimistic.ts's file header.
      qc.setQueryData(
        listKey,
        current.map((row) => (row.id === result.id ? { ...row, ...result } : row)),
      );
    },
  });
}
